import { BaseService } from "./BaseService";
import { ErrorResponse, SuccessResponse, BuildxConfig } from "../types/index";

/**
 * Storage service for Buildx
 * Handles file upload, download, and management
 * 
 * @example
 * ```typescript
 * const storage = buildx.storage();
 * 
 * // Upload file
 * const result = await storage.upload(file, 'uploads/');
 * 
 * // List files in directory
 * const files = await storage.list('uploads/');
 * 
 * // Delete file
 * await storage.delete('uploads/file.txt');
 * ```
 */
export class Storage {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * Upload file to storage
	 */
	async upload(file: File, prefix?: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const formData = new FormData();
		if (prefix && prefix !== "") {
			formData.append("prefix", prefix);
		}
		formData.append("file", file);
		formData.append("filename", file.name || "file");

		const url = this.baseService.buildProjectUrl(projectId, "/storage/upload");
		const headers = { ...(await this.baseService.getHeaders()), "Content-Type": "multipart/form-data; charset=utf-8" };

		return await this.baseService.post(url, formData, { headers });
	}

	/**
	 * Upload file to collection storage
	 */
	async uploadToCollection(file: File, collectionId: string, prefix?: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const formData = new FormData();
		if (prefix && prefix !== "") {
			formData.append("prefix", prefix);
		}
		formData.append("file", file);
		formData.append("filename", file.name || "file");
		formData.append("collection_id", collectionId);

		const url = this.baseService.buildProjectUrl(projectId, "/storage/upload");
		const headers = { ...(await this.baseService.getHeaders()), "Content-Type": "multipart/form-data; charset=utf-8" };

		return await this.baseService.post(url, formData, { headers });
	}

	/**
	 * List files in directory
	 */
	async list(path: string, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/storage?path=${encodeURIComponent(path)}`);
		return await this.baseService.get(url);
	}

	/**
	 * Get storage size for path
	 */
	async getSize(path: string, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/storage/size?path=${encodeURIComponent(path)}`);
		return await this.baseService.get(url);
	}

	/**
	 * Delete file from storage
	 */
	async delete(path: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/storage?path=${encodeURIComponent(path)}`);
		return await this.baseService.delete(url);
	}
} 